import { useSyncExternalStore, type JSX, type MouseEvent, type ReactNode } from 'react';

/** Every path the app answers. Anything else lands on the dashboard. */
export const routes = ['/', '/wagers', '/history', '/setup'] as const;
export type Route = (typeof routes)[number];

function toRoute(pathname: string): Route {
  return routes.find((route) => route === pathname) ?? '/';
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('popstate', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', listener);
  };
}

function readPath(): string {
  return window.location.pathname;
}

/** The route the browser is on, following both a navigation and the back button. */
export function useRoute(): Route {
  return toRoute(useSyncExternalStore(subscribe, readPath));
}

export function navigate(to: Route): void {
  if (window.location.pathname === to) {
    return;
  }
  window.history.pushState(null, '', to);
  window.scrollTo({ top: 0 });
  for (const listener of listeners) {
    listener();
  }
}

/** A modifier-click or a middle-click means "open it yourself", so the browser keeps it. */
function handledByBrowser(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

export interface NavLinkProps {
  readonly to: Route;
  readonly className?: string;
  readonly children: ReactNode;
}

/** A real link: it can be opened in a new tab, and it marks itself when it is the current view. */
export function NavLink({ to, className, children }: NavLinkProps): JSX.Element {
  const current = useRoute() === to;

  return (
    <a
      href={to}
      aria-current={current ? 'page' : undefined}
      onClick={(event) => {
        if (handledByBrowser(event)) {
          return;
        }
        event.preventDefault();
        navigate(to);
      }}
      className={className}
    >
      {children}
    </a>
  );
}
